/**
 * Map Service
 * Handles pure logic functionality related to map display and path visualization
 */

/**
 * Calculate which paths should be displayed based on current mode
 * @param {string} currentMode - Current mode
 * @param {Array} autoPath - Auto path array
 * @param {Array} customPath - Custom path array
 * @returns {Object} Object containing paths to display
 */
export const getVisiblePaths = (currentMode, autoPath, customPath) => {
  if (currentMode === 'Compare Paths') {
    // Compare mode shows both paths, but only confirmed custom paths
    const confirmedCustomPath = customPath && customPath._isConfirmed ? customPath : [];
    return {
      auto: autoPath,
      custom: confirmedCustomPath
    };
  } else if (currentMode === 'Auto Path') {
    // Auto mode only shows auto path
    return {
      auto: autoPath
    };
  } else if (currentMode === 'Custom Path') {
    // Custom mode shows custom path (including unconfirmed for editing)
    return {
      custom: customPath
    };
  }
  // Default: no paths shown
  return {};
};

/**
 * Determine if auto path should be displayed
 * @param {string} currentMode - Current mode
 * @returns {boolean} Whether to show auto path
 */
export const shouldShowAutoPath = (currentMode) => {
  return currentMode === 'Auto Path' || currentMode === 'Compare Paths';
};

/**
 * Determine if custom path should be displayed
 * @param {string} currentMode - Current mode
 * @returns {boolean} Whether to show custom path
 */
export const shouldShowCustomPath = (currentMode) => {
  return currentMode === 'Custom Path' || currentMode === 'Compare Paths';
};

/**
 * Determine if distance labels should be displayed
 * @param {string} currentMode - Current mode
 * @returns {boolean} Whether to show distance labels
 */
export const shouldShowDistanceLabels = (currentMode) => {
  return currentMode === 'Compare Paths' || currentMode === 'Custom Path' || currentMode === 'Auto Path';
};

/**
 * Check if there are paths to display
 * @param {boolean} shouldShowAutoPath - Whether to show auto path
 * @param {boolean} shouldShowCustomPath - Whether to show custom path
 * @param {Array} autoPath - Auto path array
 * @param {Array} customPath - Custom path array
 * @returns {Object} Object containing path display status
 */
export const hasPathsToShow = (shouldShowAutoPath, shouldShowCustomPath, autoPath, customPath) => {
  const hasAutoPaths = shouldShowAutoPath && autoPath && autoPath.length > 1;
  const hasCustomPaths = shouldShowCustomPath && customPath && customPath.length > 1;
  return { hasAutoPaths, hasCustomPaths };
};

/**
 * Get map container style class name
 * @param {string} currentMode - Current mode
 * @param {string} setupSubmode - Setup submode
 * @returns {string} Map container CSS class name
 */
export const getMapContainerClassName = (currentMode, setupSubmode) => {
  let className = "map-container";
  
  // Only show crosshair cursor in "Setup Points" mode's "Map Points" submode
  if (currentMode === 'Setup Points' && setupSubmode === 'Map Points') {
    className += " map-point-mode";
  }
  
  // Add pointer cursor in custom path mode
  if (currentMode === 'Custom Path') {
    className += " map-custom-mode";
  }
  
  return className;
}; 